// src/components/TopBar.tsx
// Top navigation bar for the admin dashboard.
// Displays page title, search input, notification bell, and user dropdown menu.

import { useNavigate, useLocation } from 'react-router-dom'
import { Bell, Search, ChevronDown, User, LogOut, Settings } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { format } from 'date-fns'

// ── Page title map ────────────────────────────────────────────

const PAGE_TITLES: Record<string, { title: string; subtitle: string }> = {
  '/': { title: 'Dashboard', subtitle: 'Church overview and analytics' },
  '/members': { title: 'Members', subtitle: 'Manage your church community' },
  '/events': { title: 'Events', subtitle: 'Create and manage church events' },
  '/sermons': { title: 'Sermons', subtitle: 'Manage sermon recordings and notes' },
  '/giving': { title: 'Giving Reports', subtitle: 'Financial records and analytics' },
  '/settings': { title: 'Settings', subtitle: 'Church info and app configuration' },
  '/profile': { title: 'My Profile', subtitle: 'Manage your personal identity' },
}

// ── Component ─────────────────────────────────────────────────

interface TopBarProps {
  onSearchChange?: (query: string) => void
}

export function TopBar({ onSearchChange }: TopBarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { profile, signOut, authLoading } = useAuthStore()

  const [searchQuery, setSearchQuery] = useState('')
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const notifRef = useRef<HTMLDivElement>(null)

  const pageInfo = PAGE_TITLES[location.pathname] ?? {
    title: 'SGS Connect',
    subtitle: '',
  }

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    onSearchChange?.(e.target.value)
  }

  const handleSignOut = async () => {
    try {
      setUserMenuOpen(false)
      await signOut()
      toast.success('Signed out successfully')
    } catch {
      toast.error('Sign out failed. Please try again.')
    }
  }

  const initials = profile?.full_name
    ? profile.full_name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U'

  const today = format(new Date(), 'EEEE, d MMMM yyyy')

  return (
    <header
      className="h-16 flex items-center justify-between px-6 flex-shrink-0 bg-white border-b"
      style={{ borderColor: 'rgba(226,232,240,1)' }}
    >
      {/* ── Left: page title ──────────────────────────────── */}
      <div className="flex flex-col justify-center min-w-0">
        <h1 className="text-lg font-bold leading-tight truncate" style={{ color: '#1A1A2E' }}>
          {pageInfo.title}
        </h1>
        <p className="text-xs text-muted-foreground hidden sm:block">
          {pageInfo.subtitle || today}
        </p>
      </div>

      {/* ── Right: actions ────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Search bar */}
        <div className="relative hidden md:flex items-center">
          <Search className="absolute left-3 w-4 h-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearch}
            className={cn(
              'h-9 pl-9 pr-4 rounded-lg text-sm bg-muted/50 border border-border',
              'w-48 focus:w-64 transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:border-transparent',
              'placeholder:text-muted-foreground'
            )}
            style={{ '--tw-ring-color': '#1E3A5F33' } as React.CSSProperties}
          />
        </div>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => setNotifOpen(!notifOpen)}
            className={cn(
              'relative w-9 h-9 rounded-lg flex items-center justify-center',
              'border border-border bg-white hover:bg-muted/50',
              'transition-colors duration-150',
              notifOpen && 'bg-muted/50'
            )}
            aria-label="Notifications"
          >
            <Bell className="w-4 h-4 text-muted-foreground" />
            {/* Unread dot */}
            <span
              className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full"
              style={{ background: '#D4A843' }}
            />
          </button>

          {/* Notifications dropdown */}
          {notifOpen && (
            <div
              className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-border z-50 overflow-hidden"
            >
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <span className="font-semibold text-sm" style={{ color: '#1A1A2E' }}>
                  Notifications
                </span>
                <button
                  className="text-xs hover:underline"
                  style={{ color: '#1E3A5F' }}
                  onClick={() => setNotifOpen(false)}
                >
                  Mark all read
                </button>
              </div>
              <div className="divide-y divide-border">
                {[
                  {
                    icon: '🙏',
                    title: 'New prayer request',
                    desc: 'A new prayer request was submitted',
                    time: '2m ago',
                    unread: true,
                  },
                  {
                    icon: '👤',
                    title: 'New member registered',
                    desc: 'A new member joined the church app',
                    time: '1h ago',
                    unread: true,
                  },
                  {
                    icon: '📅',
                    title: 'Event tomorrow',
                    desc: 'Sunday Service starts tomorrow at 9AM',
                    time: '3h ago',
                    unread: false,
                  },
                ].map((notif, i) => (
                  <div
                    key={i}
                    className={cn(
                      'flex items-start gap-3 px-4 py-3 hover:bg-muted/30 cursor-pointer transition-colors',
                      notif.unread && 'bg-blue-50/40'
                    )}
                  >
                    <div className="text-xl flex-shrink-0 mt-0.5">{notif.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium truncate" style={{ color: '#1A1A2E' }}>
                          {notif.title}
                        </p>
                        <span className="text-xs text-muted-foreground flex-shrink-0">
                          {notif.time}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{notif.desc}</p>
                    </div>
                    {notif.unread && (
                      <div
                        className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5"
                        style={{ background: '#D4A843' }}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="px-4 py-2 border-t border-border text-center">
                <button
                  className="text-xs font-medium hover:underline"
                  style={{ color: '#1E3A5F' }}
                  onClick={() => setNotifOpen(false)}
                >
                  View all notifications
                </button>
              </div>
            </div>
          )}
        </div>

        {/* User menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className={cn(
              'flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg',
              'border border-border bg-white hover:bg-muted/50',
              'transition-colors duration-150',
              userMenuOpen && 'bg-muted/50'
            )}
            aria-label="User menu"
          >
            {/* Avatar */}
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
              style={{ background: '#1E3A5F' }}
            >
              {profile?.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt={profile.full_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-xs font-bold text-white">{initials}</span>
              )}
            </div>
            <span className="text-sm font-medium hidden sm:block max-w-[120px] truncate" style={{ color: '#1A1A2E' }}>
              {profile?.full_name?.split(' ')[0] ?? 'Admin'}
            </span>
            <ChevronDown
              className={cn(
                'w-3.5 h-3.5 text-muted-foreground transition-transform duration-150',
                userMenuOpen && 'rotate-180'
              )}
            />
          </button>

          {/* User dropdown */}
          {userMenuOpen && (
            <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl shadow-xl border border-border z-50 overflow-hidden">
              {/* User info header */}
              <div className="px-4 py-3 border-b border-border">
                <p className="text-sm font-semibold truncate" style={{ color: '#1A1A2E' }}>
                  {profile?.full_name ?? 'Admin User'}
                </p>
                <p className="text-xs text-muted-foreground truncate capitalize">
                  {profile?.role ?? 'admin'} · SGS Church
                </p>
              </div>

              {/* Menu items */}
              <div className="py-1">
                <button
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-muted/50 transition-colors text-left"
                  style={{ color: '#1A1A2E' }}
                  onClick={() => {
                    setUserMenuOpen(false)
                    navigate('/profile')
                  }}
                >
                  <User className="w-4 h-4 text-muted-foreground" />
                  My Profile
                </button>
                <button
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm hover:bg-muted/50 transition-colors text-left"
                  style={{ color: '#1A1A2E' }}
                  onClick={() => {
                    setUserMenuOpen(false)
                    navigate('/settings')
                  }}
                >
                  <Settings className="w-4 h-4 text-muted-foreground" />
                  Settings
                </button>
              </div>

              <div className="border-t border-border py-1">
                <button
                  onClick={handleSignOut}
                  disabled={authLoading}
                  className={cn(
                    'flex items-center gap-3 w-full px-4 py-2.5 text-sm',
                    'hover:bg-red-50 text-red-600 transition-colors text-left',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {authLoading ? (
                    <div className="w-4 h-4 border-2 border-red-200 border-t-red-500 rounded-full animate-spin" />
                  ) : (
                    <LogOut className="w-4 h-4" />
                  )}
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
